import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from "@heroicons/react/24/outline"
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { toast } from "react-toastify"
import { ACTIVE_JOB_STATUSES, useGetJobQuery } from "~/ui-rtk/api/jobsApi"
import {
  useCreatePartnershipContentMutation,
  useCreatePartnershipMutation,
  useCreatePartnershipRowFromAdMutation,
  useDeletePartnershipContentMutation,
  useDetectPartnershipBusinessMutation,
  useGetPartnershipOverviewQuery,
  useLinkPartnershipAdMutation,
  useSetPartnershipSettingsMutation,
  useTriggerPartnershipSyncMutation,
  useUpdatePartnershipContentMutation,
  useUpdatePartnershipMutation,
} from "~/ui-rtk/api/partnershipsApi"
import type {
  CreatePartnershipContentRequest,
  CreatePartnershipRequest,
  CreatePartnershipRowFromAdRequest,
  LinkPartnershipAdRequest,
  PartnershipBusinessDetectResultDto,
  PartnershipContentRowDto,
  PartnershipEffectiveRightsStatus,
  PartnershipLastSyncDto,
  PartnershipPlatform,
  PartnershipRightsStatus,
  PartnershipSettingsDto,
  UpdatePartnershipContentRequest,
  UpdatePartnershipRequest,
} from "~/ui-rtk/api/types/partnerships.types"
import { ITEMS_PER_PAGE } from "~/ui-rtk/components/ui/charts/TableVisualization/constants"
// Direct imports (not the `common` barrel) — see CreativesTab.tsx.
import { LastUpdatedChip } from "~/ui-rtk/components/ui/common/LastUpdatedChip/LastUpdatedChip"
import Button from "~/ui-rtk/components/ui/controls/Button"
import CheckboxWithLabel from "~/ui-rtk/components/ui/controls/CheckboxWithLabel"
import Input from "~/ui-rtk/components/ui/controls/Input"
import Select from "~/ui-rtk/components/ui/controls/Select"
import useAccess from "~/ui-rtk/hooks/access"
import {
  AD_ELIGIBILITY_LABEL,
  CREATOR_APPROVAL_STEPS,
  EFFECTIVE_RIGHTS_STATUS_LABEL,
  PLATFORM_OPTIONS,
  READY_TO_LAUNCH_LABEL,
  READY_TO_LAUNCH_TOOLTIP,
  RIGHTS_STATUS_OPTIONS,
  describeUnlaunchable,
  explainSyncWarning,
  humanize,
  launchButtonLabel,
} from "./partnerships/partnership-copy"
import {
  AddContentDialog,
  AddCreatorDialog,
} from "./partnerships/PartnershipDialogs"
import PartnershipLaunchDialog from "./partnerships/PartnershipLaunchDialog"
import PartnershipSettingsInline, {
  type SaveSettingsResult,
} from "./partnerships/PartnershipSettingsInline"
import PartnershipsTable, {
  DEFAULT_SORT_DIR,
  sortRows,
  type SortKey,
  type SortState,
} from "./partnerships/PartnershipsTable"
import SyncHealthPanel from "./partnerships/SyncHealthPanel"
import UnmatchedAdsSection from "./partnerships/UnmatchedAdsSection"

/**
 * Partnerships tab — the creator / partnership ads tracker (MD-5524).
 *
 *   • One overview call feeds everything: settings, last sync, creators,
 *     content rows (with the API-derived window / eligibility state) and the
 *     partner ads no tracker row claims yet.
 *   • "Refresh from Meta" enqueues the partnership-sync job and polls it to a
 *     terminal state, then refetches the overview.
 *   • Client-side filters + sort + pagination over the content rows, same
 *     shape as CreativesTab. The default view mirrors Creator Marketing Hub →
 *     Browse Content with "Ad eligibility = ad ready": eligibility filter
 *     `ad_ready`, newest post first. Selection feeds the bulk actions: set
 *     rights status, or launch the selected posts as PAUSED partnership ads
 *     (one per post × ad set, never activated from here).
 */

type PlatformFilter = "ALL" | PartnershipPlatform
type RightsFilter = "ALL" | PartnershipEffectiveRightsStatus
/** `ALL`, `NONE` (no Meta data), or a raw Meta `ad_eligibility` value. */
type EligibilityFilter = string
/** `ALL`, `NONE` (no creator), or a partnership id. */
type CreatorFilter = string

const PLATFORM_FILTER_OPTIONS: Array<{ label: string; value: PlatformFilter }> =
  [{ label: "All platforms", value: "ALL" }, ...PLATFORM_OPTIONS]

/** Static so "Ad ready" exists (and is selectable) even before any row loads. */
export const ELIGIBILITY_FILTER_OPTIONS: Array<{
  label: string
  value: EligibilityFilter
}> = [
  { value: "ALL", label: "All Meta eligibility" },
  ...Object.entries(AD_ELIGIBILITY_LABEL).map(([value, label]) => ({
    value,
    label,
  })),
  { value: "NONE", label: "No Meta data" },
]
export const DEFAULT_ELIGIBILITY_FILTER: EligibilityFilter = "ad_ready"
export const DEFAULT_SORT: SortState = { key: "posted", dir: "desc" }

const RIGHTS_FILTER_OPTIONS: Array<{ label: string; value: RightsFilter }> = [
  { label: "All rights", value: "ALL" },
  ...RIGHTS_STATUS_OPTIONS,
  { label: EFFECTIVE_RIGHTS_STATUS_LABEL.EXPIRED, value: "EXPIRED" },
  { label: EFFECTIVE_RIGHTS_STATUS_LABEL.REVOKED, value: "REVOKED" },
]

function matchesSearch(row: PartnershipContentRowDto, query: string): boolean {
  if (!query) return true
  const haystack = [
    row.partnership?.creatorHandle,
    row.partnership?.creatorDisplayName,
    row.authorDisplayName,
    row.caption,
  ]
    .filter((v): v is string => Boolean(v))
    .join(" ")
    .toLowerCase()
  return haystack.includes(query)
}

/**
 * "Ad ready" (the default view) also shows hand-added rows: a post someone
 * pasted in via Add content or created from an unmatched ad carries no Meta
 * eligibility yet, and hiding it right after the "Post added" toast would
 * make the add look like it failed. Everything else is a plain match.
 */
export function matchesEligibility(
  row: PartnershipContentRowDto,
  filter: EligibilityFilter,
): boolean {
  if (filter === "ALL") return true
  if (filter === "NONE") return !row.adEligibility
  if (filter === "ad_ready" && row.source === "MANUAL" && !row.adEligibility) {
    return true
  }
  return row.adEligibility === filter
}

function matchesCreator(
  row: PartnershipContentRowDto,
  filter: CreatorFilter,
): boolean {
  if (filter === "ALL") return true
  if (filter === "NONE") return !row.partnership
  return row.partnership?.id === filter
}

/**
 * Why discovery could not have produced rows, in plain language — or null when
 * the last sync's Instagram discovery ran (or never ran at all).
 */
export function discoveryProblem(
  settings: PartnershipSettingsDto,
  lastSync: PartnershipLastSyncDto | null,
): string | null {
  if (settings.businessId === null) {
    return "Meta content discovery needs a Business Manager id — set one in the panel above, then Refresh from Meta."
  }
  if (lastSync?.status === "FAILED") {
    return `The last sync failed${lastSync.error ? ` (${explainSyncWarning(lastSync.error, settings.credentialKind)})` : ""} — fix the cause, then Refresh from Meta.`
  }
  if (lastSync?.status !== "COMPLETED" || !lastSync.discovery) return null
  const ig = lastSync.discovery.instagram
  if (ig.status === "ok" || ig.status === "partial") return null
  const reason = ig.detail ? explainSyncWarning(ig.detail) : null
  // A skip for a missing brand Instagram account is fixed on the connector,
  // not in the Business Manager panel; everything else is keyed by that id.
  const fixHint =
    ig.status === "skipped" &&
    /no brand instagram account/i.test(ig.detail ?? "")
      ? "Fix it on the Instagram Insights connector, then Refresh from Meta."
      : "Fix it in the Business Manager panel above, then Refresh from Meta."
  return `Instagram discovery was ${ig.status}${reason ? `: ${reason}` : ""} ${fixHint}`
}

interface BulkActionBarProps {
  selectedCount: number
  applying: boolean
  /** Why "Launch {n} as ads" is disabled, or null when every selected row can launch. */
  launchBlockedReason: string | null
  onClear: () => void
  onSetRights: (status: PartnershipRightsStatus) => void
  onLaunch: () => void
}
function BulkActionBar({
  selectedCount,
  applying,
  launchBlockedReason,
  onClear,
  onSetRights,
  onLaunch,
}: BulkActionBarProps) {
  if (selectedCount === 0) {
    return (
      <p className="text-system-gray-300 text-sm">
        Select posts to set their rights status in bulk or launch them as paused
        partnership ads.
      </p>
    )
  }
  return (
    <div className="flex items-center justify-between rounded-md border border-system-blue-600 bg-system-blue-700 px-system-md py-system-sm">
      <span className="text-sm text-white">
        {selectedCount} post{selectedCount === 1 ? "" : "s"} selected
      </span>
      <div className="flex items-center gap-system-sm">
        <button
          type="button"
          onClick={onClear}
          className="text-sm text-system-gray-200 hover:text-white"
        >
          Clear
        </button>
        <Select
          aria-label="Set rights status for selected posts"
          options={[
            { value: "", label: "Set rights status…" },
            ...RIGHTS_STATUS_OPTIONS,
          ]}
          value=""
          disabled={applying}
          onChange={e => {
            if (e.target.value) {
              onSetRights(e.target.value as PartnershipRightsStatus)
            }
          }}
          className="h-9 w-48 text-sm"
        />
        {/* Disabled buttons swallow hover, so the tooltip lives on the wrapper. */}
        <span
          title={
            launchBlockedReason ??
            "Create one paused partnership ad per selected post in ad sets you pick"
          }
        >
          <Button
            size="xs"
            variant={{ variant: "solid", color: "blue" }}
            disabled={applying || launchBlockedReason !== null}
            onClick={onLaunch}
          >
            {launchButtonLabel(selectedCount)}
          </Button>
        </span>
      </div>
    </div>
  )
}

export default function PartnershipsTab() {
  const { isSuperAdmin } = useAccess()
  const {
    data: overview,
    isLoading,
    error,
    refetch,
  } = useGetPartnershipOverviewQuery()

  const [updatePartnership] = useUpdatePartnershipMutation()
  const [updateContent] = useUpdatePartnershipContentMutation()
  const [deleteContent] = useDeletePartnershipContentMutation()
  const [createPartnership] = useCreatePartnershipMutation()
  const [createContent] = useCreatePartnershipContentMutation()
  const [linkAd] = useLinkPartnershipAdMutation()
  const [createRowFromAd] = useCreatePartnershipRowFromAdMutation()
  const [setSettings] = useSetPartnershipSettingsMutation()
  const [detectBusiness] = useDetectPartnershipBusinessMutation()
  const [triggerSync, { isLoading: isSyncStarting }] =
    useTriggerPartnershipSyncMutation()

  // ----- Refresh from Meta: enqueue, poll to terminal, refetch -----
  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  // Jobs already seen through to a terminal state — keeps the resume effect
  // below from re-polling a job the overview still reports as running while
  // its own refetch is in flight.
  const handledJobIds = useRef(new Set<string>())
  const { data: activeJob } = useGetJobQuery(activeJobId ?? "", {
    skip: !activeJobId,
    pollingInterval: activeJobId ? 4000 : 0,
  })

  const lastSync = overview?.lastSync ?? null

  // A sync started elsewhere (nightly schedule, another tab) that is still
  // running when this tab mounts gets picked up and polled too.
  useEffect(() => {
    if (!lastSync || activeJobId) return
    if (!(ACTIVE_JOB_STATUSES as string[]).includes(lastSync.status)) return
    if (handledJobIds.current.has(lastSync.jobRunId)) return
    setActiveJobId(lastSync.jobRunId)
  }, [lastSync, activeJobId])

  useEffect(() => {
    const status = activeJob?.status
    if (!activeJobId || !status || ACTIVE_JOB_STATUSES.includes(status)) {
      return
    }
    handledJobIds.current.add(activeJobId)
    setActiveJobId(null)
    void refetch()
    if (status === "COMPLETED") {
      toast.success("Partnerships refreshed from Meta.")
    } else {
      toast.error(
        `Partnership sync ${status.toLowerCase()}${activeJob?.error ? `: ${activeJob.error}` : "."}`,
      )
    }
    // Fire only on the status transition — the job payload arrives in the
    // same response that flips it terminal.
  }, [activeJob?.status])

  const handleRefresh = useCallback(async () => {
    try {
      const { jobRunId } = await triggerSync().unwrap()
      toast.success("Refreshing partnerships from Meta — this takes a minute.")
      setActiveJobId(jobRunId)
    } catch {
      toast.error("Could not start the partnership sync. Please try again.")
    }
  }, [triggerSync])

  // ----- Mutation wrappers: toast + boolean result for the editors -----
  const savePartnership = useCallback(
    async (id: string, body: UpdatePartnershipRequest) => {
      try {
        await updatePartnership({ id, body }).unwrap()
        toast.success("Creator updated")
        return true
      } catch {
        toast.error("Couldn't update the creator")
        return false
      }
    },
    [updatePartnership],
  )

  const saveContent = useCallback(
    async (id: string, body: UpdatePartnershipContentRequest) => {
      try {
        await updateContent({ id, body }).unwrap()
        toast.success("Post updated")
        return true
      } catch {
        toast.error("Couldn't update the post")
        return false
      }
    },
    [updateContent],
  )

  const removeContent = useCallback(
    async (row: PartnershipContentRowDto) => {
      try {
        await deleteContent(row.id).unwrap()
        toast.success("Post removed")
        return true
      } catch {
        toast.error("Couldn't remove the post")
        return false
      }
    },
    [deleteContent],
  )

  const addCreator = useCallback(
    async (body: CreatePartnershipRequest) => {
      try {
        await createPartnership(body).unwrap()
        toast.success(`@${body.creatorHandle} added`)
        return true
      } catch {
        toast.error("Couldn't add the creator")
        return false
      }
    },
    [createPartnership],
  )

  const addContent = useCallback(
    async (body: CreatePartnershipContentRequest) => {
      try {
        await createContent(body).unwrap()
        toast.success("Post added")
        return true
      } catch {
        toast.error("Couldn't add the post")
        return false
      }
    },
    [createContent],
  )

  const handleLinkAd = useCallback(
    async (adId: string, body: LinkPartnershipAdRequest) => {
      try {
        await linkAd({ id: adId, body }).unwrap()
        toast.success("Ad linked")
        return true
      } catch {
        toast.error("Couldn't link the ad")
        return false
      }
    },
    [linkAd],
  )

  const handleCreateRowFromAd = useCallback(
    async (adId: string, body: CreatePartnershipRowFromAdRequest) => {
      try {
        await createRowFromAd({ id: adId, body }).unwrap()
        toast.success(`Tracker row created for @${body.creatorHandle}`)
        return true
      } catch {
        toast.error("Couldn't create the tracker row")
        return false
      }
    },
    [createRowFromAd],
  )

  const saveSettings = useCallback(
    async (businessId: string | null): Promise<SaveSettingsResult> => {
      try {
        const saved = await setSettings({ businessId }).unwrap()
        toast.success(
          businessId
            ? "Business Manager id saved"
            : "Business Manager override removed",
        )
        return { ok: true, verification: saved.verification ?? null }
      } catch {
        toast.error(
          "Couldn't save the Business Manager id — is the Instagram Insights connector connected?",
        )
        return { ok: false, verification: null }
      }
    },
    [setSettings],
  )

  const handleDetectBusiness =
    useCallback(async (): Promise<PartnershipBusinessDetectResultDto | null> => {
      try {
        return await detectBusiness().unwrap()
      } catch {
        toast.error(
          "Couldn't detect Business Managers — check the Instagram Insights connector and try again.",
        )
        return null
      }
    }, [detectBusiness])

  // ----- Search + filters -----
  const [searchInput, setSearchInput] = useState("")
  const [searchQuery, setSearchQuery] = useState("")
  useEffect(() => {
    const trimmed = searchInput.trim().toLowerCase()
    const timer = setTimeout(() => setSearchQuery(trimmed), 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  const [platformFilter, setPlatformFilter] = useState<PlatformFilter>("ALL")
  const [rightsFilter, setRightsFilter] = useState<RightsFilter>("ALL")
  const [eligibilityFilter, setEligibilityFilter] = useState<EligibilityFilter>(
    DEFAULT_ELIGIBILITY_FILTER,
  )
  const [creatorFilter, setCreatorFilter] = useState<CreatorFilter>("ALL")
  // "Ready to launch": rows with no launch blockers (`derived.blastable`).
  const [readyOnly, setReadyOnly] = useState(false)
  const [sort, setSort] = useState<SortState | null>(DEFAULT_SORT)

  const rows = useMemo(() => overview?.content ?? [], [overview])

  const creatorOptions = useMemo(() => {
    const present = new Map<string, string>()
    let hasNone = false
    for (const row of rows) {
      if (row.partnership) {
        present.set(row.partnership.id, `@${row.partnership.creatorHandle}`)
      } else {
        hasNone = true
      }
    }
    const options = [...present.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label))
    return [
      { value: "ALL", label: "All creators" },
      ...options,
      ...(hasNone ? [{ value: "NONE", label: "No creator" }] : []),
    ]
  }, [rows])

  // The eligibility default is a view, not a filter the user applied — "Clear
  // filters" returns to it rather than to "All".
  const hasActiveFilters =
    platformFilter !== "ALL" ||
    rightsFilter !== "ALL" ||
    eligibilityFilter !== DEFAULT_ELIGIBILITY_FILTER ||
    creatorFilter !== "ALL" ||
    readyOnly

  const clearFilters = useCallback(() => {
    setPlatformFilter("ALL")
    setRightsFilter("ALL")
    setEligibilityFilter(DEFAULT_ELIGIBILITY_FILTER)
    setCreatorFilter("ALL")
    setReadyOnly(false)
  }, [])

  const toggleSort = useCallback((key: SortKey) => {
    setSort(prev => {
      if (!prev || prev.key !== key) return { key, dir: DEFAULT_SORT_DIR[key] }
      if (prev.dir === DEFAULT_SORT_DIR[key]) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" }
      }
      return null
    })
  }, [])

  const visibleRows = useMemo(() => {
    const filtered = rows.filter(
      r =>
        (platformFilter === "ALL" || r.platform === platformFilter) &&
        (rightsFilter === "ALL" ||
          r.derived.effectiveRightsStatus === rightsFilter) &&
        matchesEligibility(r, eligibilityFilter) &&
        matchesCreator(r, creatorFilter) &&
        (!readyOnly || r.derived.blastable) &&
        matchesSearch(r, searchQuery),
    )
    return sortRows(filtered, sort)
  }, [
    rows,
    platformFilter,
    rightsFilter,
    eligibilityFilter,
    creatorFilter,
    readyOnly,
    searchQuery,
    sort,
  ])

  // ----- Selection (scoped to visible rows, like CreativesTab) -----
  const [selected, setSelected] = useState<Set<string>>(new Set())
  useEffect(() => {
    setSelected(prev => {
      if (prev.size === 0) return prev
      const visible = new Set(visibleRows.map(r => r.id))
      const next = new Set([...prev].filter(id => visible.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [visibleRows])

  const toggleRow = useCallback((id: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const toggleAll = useCallback((ids: string[]) => {
    setSelected(prev => {
      const allOnPage = ids.every(id => prev.has(id))
      const next = new Set(prev)
      if (allOnPage) for (const id of ids) next.delete(id)
      else for (const id of ids) next.add(id)
      return next
    })
  }, [])

  const clearSelection = useCallback(() => setSelected(new Set()), [])

  const [applyingBulk, setApplyingBulk] = useState(false)
  const applyBulkRights = useCallback(
    async (status: PartnershipRightsStatus) => {
      const partnershipIds = new Set<string>()
      let skipped = 0
      for (const row of rows) {
        if (!selected.has(row.id)) continue
        if (row.partnership) partnershipIds.add(row.partnership.id)
        else skipped += 1
      }
      if (partnershipIds.size === 0) {
        toast.error("None of the selected posts is linked to a creator")
        return
      }
      setApplyingBulk(true)
      const results = await Promise.allSettled(
        [...partnershipIds].map(id =>
          updatePartnership({ id, body: { rightsStatus: status } }).unwrap(),
        ),
      )
      setApplyingBulk(false)
      const failed = results.filter(r => r.status === "rejected").length
      const done = results.length - failed
      if (done > 0) {
        toast.success(
          `Rights status set for ${done} creator${done === 1 ? "" : "s"}${
            skipped > 0
              ? ` (${skipped} post${skipped === 1 ? "" : "s"} without a creator skipped)`
              : ""
          }`,
        )
      }
      if (failed > 0) {
        toast.error(
          `Couldn't update ${failed} creator${failed === 1 ? "" : "s"}`,
        )
      }
    },
    [rows, selected, updatePartnership],
  )

  // ----- Pagination -----
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: ITEMS_PER_PAGE,
  })
  useEffect(() => {
    setPagination(prev =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    )
  }, [
    searchQuery,
    platformFilter,
    rightsFilter,
    eligibilityFilter,
    creatorFilter,
    readyOnly,
    sort,
  ])

  const totalPages = Math.max(
    1,
    Math.ceil(visibleRows.length / pagination.pageSize),
  )
  const safePageIndex = Math.min(pagination.pageIndex, totalPages - 1)
  const paginatedRows = useMemo(() => {
    const start = safePageIndex * pagination.pageSize
    return visibleRows.slice(start, start + pagination.pageSize)
  }, [visibleRows, safePageIndex, pagination.pageSize])

  // ----- Dialogs -----
  const [addCreatorOpen, setAddCreatorOpen] = useState(false)
  const [addContentOpen, setAddContentOpen] = useState(false)
  // Rows being launched as PAUSED partnership ads; null = dialog closed.
  const [launchRows, setLaunchRows] = useState<
    PartnershipContentRowDto[] | null
  >(null)

  const selectedRows = useMemo(
    () => visibleRows.filter(r => selected.has(r.id)),
    [visibleRows, selected],
  )
  const launchSelected = useCallback(() => {
    if (selectedRows.length > 0) setLaunchRows(selectedRows)
  }, [selectedRows])
  // The job created the ads (or is still creating them): the Ads column
  // reads from the overview, so refetch; the selection has done its job.
  const handleLaunched = useCallback(() => {
    void refetch()
    setSelected(new Set())
  }, [refetch])

  if (isLoading || (!overview && !error)) {
    return (
      <div className="p-system-md text-system-gray-300">
        Loading partnerships…
      </div>
    )
  }

  if (error || !overview) {
    return (
      <div className="p-system-md text-system-red-500">
        Failed to load partnerships. Check the Meta connector status and
        refresh.
      </div>
    )
  }

  const { settings } = overview
  const launchBlockedReason = describeUnlaunchable(
    selectedRows,
    settings.blastAvailable,
  )

  // ----- Empty state: say WHY nothing shows, not just that nothing does -----
  const problem = discoveryProblem(settings, lastSync)
  const onlyDefaultEligibility =
    !searchInput &&
    platformFilter === "ALL" &&
    rightsFilter === "ALL" &&
    creatorFilter === "ALL" &&
    !readyOnly &&
    eligibilityFilter === DEFAULT_ELIGIBILITY_FILTER
  let emptyStateCopy: ReactNode
  if (rows.length === 0) {
    if (problem) {
      emptyStateCopy = (
        <span data-testid="empty-discovery-problem">{problem}</span>
      )
    } else if (lastSync?.status === "COMPLETED") {
      emptyStateCopy = (
        <span data-testid="empty-no-ad-ready">
          No ad-ready creator posts yet. {CREATOR_APPROVAL_STEPS}
        </span>
      )
    } else {
      emptyStateCopy =
        "No partnership posts yet. Refresh from Meta to discover creator posts, or add one by hand."
    }
  } else if (searchInput) {
    emptyStateCopy = `No posts match "${searchInput.trim()}".`
  } else if (onlyDefaultEligibility) {
    const hidden = rows.length
    emptyStateCopy = (
      <span
        data-testid="empty-no-ad-ready"
        className="inline-flex flex-wrap items-center justify-center gap-x-1"
      >
        {problem ?? `No ad-ready creator posts yet. ${CREATOR_APPROVAL_STEPS}`}{" "}
        <span>
          {hidden} post{hidden === 1 ? "" : "s"} with other Meta eligibility{" "}
          {hidden === 1 ? "is" : "are"} hidden.
        </span>
        <button
          type="button"
          onClick={() => setEligibilityFilter("ALL")}
          className="font-medium text-brand hover:underline"
        >
          Show all posts
        </button>
      </span>
    )
  } else {
    emptyStateCopy = "No posts match the current filters."
  }

  const isSyncing = Boolean(activeJobId)

  return (
    <div className="flex flex-col gap-system-sm">
      <div className="flex flex-wrap items-center justify-between gap-system-sm">
        <div className="flex min-w-0 flex-wrap items-center gap-system-sm">
          <h2 className="text-lg font-semibold text-white">Partnerships</h2>
          <LastUpdatedChip lastDate={lastSync?.completedAt ?? undefined}>
            Last synced
          </LastUpdatedChip>
          {isSyncing && (
            <span
              role="status"
              className="inline-flex items-center gap-1 text-xs text-system-gray-300"
            >
              <span className="loading loading-spinner loading-xs" />
              Syncing with Meta
              {activeJob?.step ? ` · ${humanize(activeJob.step)}` : ""}…
            </span>
          )}
          {!isSyncing && lastSync?.status === "FAILED" && (
            <span
              className="text-xs text-system-red-400"
              title={lastSync.summary ?? undefined}
            >
              Last sync failed{lastSync.summary ? `: ${lastSync.summary}` : ""}
            </span>
          )}
          {!isSyncing &&
            lastSync?.status === "COMPLETED" &&
            lastSync.summary && (
              <span
                className="max-w-xl truncate text-xs text-system-gray-400"
                title={lastSync.summary}
              >
                {lastSync.summary}
              </span>
            )}
        </div>
        <div className="flex items-center gap-system-sm">
          <Button
            size="xs"
            variant={{ variant: "solid", color: "blue" }}
            onClick={() => setAddCreatorOpen(true)}
          >
            <PlusIcon className="h-4 w-4" />
            Add creator
          </Button>
          <Button
            size="xs"
            variant={{ variant: "solid", color: "blue" }}
            onClick={() => setAddContentOpen(true)}
          >
            <PlusIcon className="h-4 w-4" />
            Add content
          </Button>
          <Button
            size="xs"
            variant={{ variant: "solid", color: "pink" }}
            onClick={() => void handleRefresh()}
            disabled={isSyncStarting || isSyncing}
            title="Pull creator permissions, discovered posts and partner ads from Meta"
          >
            <ArrowPathIcon className="h-4 w-4" />
            {isSyncStarting ? "Starting…" : "Refresh from Meta"}
          </Button>
        </div>
      </div>

      {settings.businessId === null && (
        <div className="flex flex-col gap-2 rounded-md border border-system-yellow-600 bg-system-yellow-900/30 px-system-md py-system-sm text-xs text-system-yellow-100">
          <span className="font-medium">
            Meta content discovery needs a Business Manager id
          </span>
          <span>
            Creator permissions and partner ads still sync, but discovered posts
            won&apos;t appear until a Business Manager id is set
            {isSuperAdmin
              ? " — use Detect below or enter one."
              : " — ask a Marathon admin."}
          </span>
        </div>
      )}

      {/* Everyone sees which business discovery runs through and who is connected;
          only super-admins can change it (the PUT / detect routes are super-admin). */}
      <PartnershipSettingsInline
        settings={settings}
        canEdit={isSuperAdmin}
        onSave={saveSettings}
        onDetect={handleDetectBusiness}
      />

      {!isSyncing && (
        <SyncHealthPanel
          lastSync={lastSync}
          canEdit={isSuperAdmin}
          credentialKind={settings.credentialKind}
        />
      )}

      <div className="flex flex-wrap items-center gap-system-sm">
        <Input
          fullWidth
          type="search"
          aria-label="Search partnerships"
          placeholder="Search by creator handle or caption…"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          prependNode={
            <MagnifyingGlassIcon className="h-4 w-4 text-system-gray-300" />
          }
          className="min-w-64 flex-1"
        />
        <Select
          aria-label="Filter by platform"
          options={PLATFORM_FILTER_OPTIONS}
          value={platformFilter}
          onChange={e => setPlatformFilter(e.target.value as PlatformFilter)}
          className="h-9 w-40 text-sm"
        />
        <Select
          aria-label="Filter by rights status"
          options={RIGHTS_FILTER_OPTIONS}
          value={rightsFilter}
          onChange={e => setRightsFilter(e.target.value as RightsFilter)}
          className="h-9 w-40 text-sm"
        />
        <Select
          aria-label="Filter by Meta eligibility"
          options={ELIGIBILITY_FILTER_OPTIONS}
          value={eligibilityFilter}
          onChange={e => setEligibilityFilter(e.target.value)}
          className="h-9 w-40 text-sm"
        />
        <Select
          aria-label="Filter by creator"
          options={creatorOptions}
          value={creatorFilter}
          onChange={e => setCreatorFilter(e.target.value)}
          className="h-9 w-40 text-sm"
        />
        <CheckboxWithLabel
          checked={readyOnly}
          onChange={e => setReadyOnly(e.target.checked)}
        >
          <span
            className="whitespace-nowrap text-xs text-system-gray-200"
            title={READY_TO_LAUNCH_TOOLTIP}
          >
            {READY_TO_LAUNCH_LABEL}
          </span>
        </CheckboxWithLabel>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="whitespace-nowrap text-xs font-medium text-system-gray-300 hover:text-white"
          >
            Clear filters
          </button>
        )}
        {(searchInput || hasActiveFilters) && (
          <span className="whitespace-nowrap text-xs text-system-gray-300">
            {visibleRows.length} result{visibleRows.length === 1 ? "" : "s"}
          </span>
        )}
      </div>

      <BulkActionBar
        selectedCount={selected.size}
        applying={applyingBulk}
        launchBlockedReason={launchBlockedReason}
        onClear={clearSelection}
        onSetRights={status => void applyBulkRights(status)}
        onLaunch={launchSelected}
      />

      <PartnershipsTable
        rows={paginatedRows}
        selected={selected}
        onToggleRow={toggleRow}
        onToggleAll={toggleAll}
        sort={sort}
        onToggleSort={toggleSort}
        onSavePartnership={savePartnership}
        onSaveContent={saveContent}
        onDeleteContent={removeContent}
        emptyStateCopy={emptyStateCopy}
        launchAvailable={settings.blastAvailable}
        onLaunch={row => setLaunchRows([row])}
        pagination={{
          pageIndex: safePageIndex,
          pageSize: pagination.pageSize,
          totalPages,
          onNext: () =>
            setPagination(prev => ({
              ...prev,
              pageIndex: Math.min(prev.pageIndex + 1, totalPages - 1),
            })),
          onPrevious: () =>
            setPagination(prev => ({
              ...prev,
              pageIndex: Math.max(prev.pageIndex - 1, 0),
            })),
          onChangeState: partial =>
            setPagination(prev => ({ ...prev, ...partial })),
        }}
      />

      <UnmatchedAdsSection
        ads={overview.unmatchedAds}
        contentRows={rows}
        onLink={handleLinkAd}
        onCreateRow={handleCreateRowFromAd}
      />

      <AddCreatorDialog
        open={addCreatorOpen}
        onClose={() => setAddCreatorOpen(false)}
        onSubmit={addCreator}
      />
      <AddContentDialog
        open={addContentOpen}
        partnerships={overview.partnerships}
        onClose={() => setAddContentOpen(false)}
        onSubmit={addContent}
      />
      {launchRows && (
        <PartnershipLaunchDialog
          rows={launchRows}
          onClose={() => setLaunchRows(null)}
          onLaunched={handleLaunched}
        />
      )}
    </div>
  )
}
